import React, { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import { Box, IconButton, Slider, Typography, CircularProgress } from "@mui/material";
import { PlayArrow, Pause, Fullscreen, VolumeUp, VolumeOff } from "@mui/icons-material";
import screenfull from "screenfull";

export default function VideoPlayerArea({
  url,
  onPlayToggle,
  onHeightChange,
  thumbnail,
  playerRef,
  ...props
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);

  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);   // 加载中
  const [isReady, setIsReady] = useState(false);      // 可播放

  useEffect(() => {
    setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent))
  }, []);

  // 初始化 HLS
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    let hlsInstance = null;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsInstance = hls;
      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
    }

    // 监听加载状态
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => {
      setIsLoading(false);
      setIsReady(true);
    };

    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);

    return () => {
      if (hlsInstance) hlsInstance.destroy();
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [url]);

  // 播放/暂停
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
    onPlayToggle?.();
  };

  // 全屏
  const handleFullscreen = () => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video) return;

    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
      return;
    }

    if (screenfull.isEnabled && container) {
      screenfull.toggle(container);
      return;
    }

    if (video.requestFullscreen) {
      video.requestFullscreen();
      return;
    }
  };

  // 音量
  const handleVolumeChange = (e, newValue) => {
    const video = videoRef.current;
    setVolume(newValue);
    if (video) video.volume = newValue;
    setIsMuted(newValue === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
      if (video.volume === 0) {
        video.volume = 0.5;
        setVolume(0.5);
      }
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  // 进度条
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    setDuration(video.duration || 0);
    setProgress((video.currentTime / (video.duration || 1)) * 100);
  };

  const handleSeek = (e, newValue) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = (newValue / 100) * (video.duration || 1);
    setProgress(newValue);
  };

  // 控制条显示/隐藏
  const hideControlsTimeout = useRef(null);
  const handleMouseMove = () => {
    if (!isMobile) {
      setShowControls(true);
      clearTimeout(hideControlsTimeout.current);
      hideControlsTimeout.current = setTimeout(() => setShowControls(false), 2500);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        overflow: "hidden",
        height: "32vh",
      }}
      onMouseMove={handleMouseMove}
      >

      <video
        ref={(el) => {
          videoRef.current = el;
          if (playerRef) playerRef.current = el; // ✅ 把引用同步给父组件
        }}
        muted={isMuted}
        playsInline
        preload="auto"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
        }}
        onTimeUpdate={handleTimeUpdate}
      />

      {/* 加载中显示进度条 */}
      {isLoading && !isReady && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      )}

      {/* 视频准备好后显示播放按钮（未播放时） */}
      {isReady && !isPlaying && (
        <IconButton
          onClick={togglePlay}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <PlayArrow fontSize="large" />
        </IconButton>
      )}

      {/* 控制条 */}
      {showControls && isReady && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 0.25, sm: 1 },
            bgcolor: "rgba(0,0,0,0.6)",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 0.25, sm: 1 },
          }}
        >
          {/* PC 端：进度条独立一行 */}
          {!isMobile && (
            <Slider
              value={progress}
              onChange={handleSeek}
              sx={{
                color: "red",
                height: 4,
                "& .MuiSlider-thumb": { display: "none" },
              }}
            />
          )}

          {/* 控件区 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              width: "100%",
            }}
          >
            <IconButton onClick={togglePlay} sx={{ color: "#fff" }} size="small">
              {isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>

            {/* 移动端进度条 */}
            {isMobile ? (
              <Slider
                value={progress}
                onChange={handleSeek}
                sx={{
                  flexGrow: 1,
                  color: "red",
                  height: 2,
                  "& .MuiSlider-thumb": { display: "none" },
                }}
              />
            ) : (
              <>
                {/* PC 音量控制 */}
                <IconButton onClick={toggleMute} sx={{ color: "#fff" }} size="small">
                  {isMuted || volume === 0 ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
                </IconButton>
                <Slider
                  value={isMuted ? 0 : volume}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={handleVolumeChange}
                  sx={{ width: 100, color: "white" }}
                />
              </>
            )}

            {isMobile && (
              <IconButton onClick={toggleMute} sx={{ color: "#fff" }} size="small">
                {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
              </IconButton>
            )}

            {/* 时间显示 */}
            <Typography
              variant="body2"
              color="white"
              sx={{ ml: 1, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {/* 全屏 */}
            <IconButton onClick={handleFullscreen} sx={{ color: "#fff" }} size="small">
              <Fullscreen fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function formatTime(time) {
  if (isNaN(time)) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}
